import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

export interface BadgeProps {
  variant?: BadgeVariant;
  dot?: boolean;
  className?: string;
  children: ReactNode;
}

const variantMap: Record<BadgeVariant, string> = {
  neutral: 'bg-bg-surface-3 text-text-secondary border-border-subtle',
  success: 'bg-success-muted text-success border-success/30',
  warning: 'bg-warning-muted text-warning border-warning/30',
  danger: 'bg-danger-muted text-danger border-danger/30',
  info: 'bg-info-muted text-info border-info/30',
  accent: 'bg-accent-muted text-text-primary border-accent-base/50',
};

const dotColor: Record<BadgeVariant, string> = {
  neutral: 'bg-text-tertiary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  accent: 'bg-accent-base',
};

export function Badge({ variant = 'neutral', dot = false, className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        variantMap[variant],
        className,
      )}
    >
      {dot && <span className={cn('inline-block size-1.5 rounded-full', dotColor[variant])} aria-hidden />}
      {children}
    </span>
  );
}
